import KeenSlider from "keen-slider";
import "keen-slider/keen-slider.min.css";

/* global gsap */
/* global Draggable */

/**
  Class for managing the Keen Slider and animation data for the Flavor Slider.
  --- does not use keen slider or another third-party slider library
  --- uses gsap animation library to simulate effect of a slider

  Currently on Ingredients Page Only
*/
export class flavorsSlider {
  constructor() {
    this.breakpoint = 1200;
    this.loading = true;
    this.wrapper = document.getElementById("flavor-slider-wrapper");
    this.section = document.getElementById("flavor-slider-section");
    this.sliderEls = {
      draggableInstance: false,
      spinner: document.getElementById("flavor-spinner"),
      track: document.getElementById("track"),
      allSlides: document.querySelectorAll("[data-flavor-slide]"),
      slider: null,
    };
    this.controlEls = {
      container: document.getElementById("flavor-slider-controls"),
      next: document.getElementById("flavor-control-next"),
      prev: document.getElementById("flavor-control-prev"),
    };
    this.contentEls = {
      container: document.querySelectorAll("#flavor-content"),
      title: document.querySelectorAll("#flavor-title"),
      text: document.querySelectorAll("#flavor-text"),
      button: document.querySelectorAll("#flavor-button"),
    };
    this.blobEls = {
      container: document.getElementById("flavor-blob-container"),
      blobs: {
        mobile: document.getElementById("flavor-blob-m"),
        desktop: document.getElementById("flavor-blob-d"),
      },
    };
    this.animData = [];
    this.animPositions = {
      medianIndex: null,
      onScreenAll: document.querySelectorAll("[data-flavor-anim-position]"),
      offScreen: {
        left: null,
        right: null,
      },
    };
    this.state = {
      colors: {
        background: null,
        blob: null,
      },
      btn: {
        lable: null,
        url: null,
      },
      title: null,
      text: null,
      slide: null,
      index: null,
    };

    this._init();
  }

  /**
    Sets the median position of an array of objects, and stores it in the "animPositions" property of the calling object.
    @param {Array} arr - An array of objects with numeric keys to determine the median position.
    @returns {Number} The index of the median object in the array.
  */
  _setMedianPosition = (arr) => {
    // Calculate the middle index of the array
    const mid = Math.floor(arr.length / 2);
    // Calculate the median position based on whether the array length is odd or even
    const slide =
      arr.length % 2 !== 0 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;

    // Store the index of the median position in the "animPositions" property of the calling object
    this.animPositions.medianIndex = slide.key;

    return this.animPositions.medianIndex;
  };

  /**
    Sets the animation positions for the elements on and off screen,
    and initializes the animation data for each on-screen element.
  */
  _setAnimPositions = () => {
    // Set the off-screen positions for the left and right elements
    this.animPositions.offScreen.left = document.querySelector(
      '[ data-flavor-anim-offscreen="left"]'
    ).offsetLeft;
    this.animPositions.offScreen.right = document.querySelector(
      '[ data-flavor-anim-offscreen="right"]'
    ).offsetLeft;

    // Initialize the animation data for each on-screen element
    this.animPositions.onScreenAll.forEach((can, index) => {
      this.animData.push({
        key: index,
        x: can.offsetLeft,
        posX: can.offsetLeft,
        width: can.offsetWidth,
        scale: can.dataset.scale,
        opacity: can.dataset.opacity,
      });
    });

    // Calculate the median position of the on-screen elements
    this._setMedianPosition(this.animData);

    // Set the slides using the animation data
    this._setSlides();
  };

  /**
    Sets the positions, scale, and opacity of each slide element based on its animation data.
    If the animation data for a slide does not exist, it is placed offscreen to the right.
    If a slide is in the center position, its data is updated and displayed.
  */
  _setSlides = () => {
    // component does not show same flavor twice or clone cans
    if (
      this.sliderEls.allSlides.length < this.animPositions.onScreenAll.length
    ) {
      console.error("less than minimum number of slides");
      return;
    }

    // Position each slide based on its animation data
    this.sliderEls.allSlides.forEach((slide) => {
      const animPosition = this.animData[slide.dataset.pos];
      const isCenterSlide =
        parseInt(slide.dataset.pos) === this.animPositions.medianIndex;

      if (animPosition === undefined) {
        // if animation position does not exist place slide offscreen to the right
        gsap.set(slide, { left: this.animPositions.offScreen.right });
      } else {
        // place slide on position marker
        const posX = isCenterSlide
          ? `${animPosition.x}px`
          : `${
              animPosition.x + (animPosition.width / 2 - slide.offsetWidth / 2)
            }px`;
        this.animData[slide.dataset.pos].x = posX;
        slide.style.left = posX;
        slide.style.transform = `scale(${animPosition.scale})`;
        slide.style.opacity = animPosition.opacity;
      }

      // if slide is in the middle of the row display it's data
      if (isCenterSlide) {
        slide.removeAttribute("aria-hidden");
        this._updateData(slide);
      }
    });

    // update section colors
    gsap.set(this.section, { background: this.state.colors.background });
    gsap.set(this.blobEls.blobs.desktop, { fill: this.state.colors.blob });

    this._displayData();

    this._addControls();
  };

  /**
    Creates a Draggable instance and attaches it to the wrapper element.
    Detects horizontal drag direction and triggers animation of the slide elements in the appropriate direction.
  */
  _setDraggable = () => {
    let startX, startY;

    const proxy = document.createElement("div");

    const that = this;

    this.sliderEls.draggableInstance = Draggable.create(proxy, {
      trigger: this.wrapper,
      type: "x",
      edgeResistance: 0.65,
      onPress: function () {
        // record the starting values so we can compare them later...
        startX = this.x;
        startY = this.y;
      },
      onRelease: function () {
        const xChange = this.x - startX;
        const yChange = this.y - startY;
        const ratio = Math.abs(xChange / yChange);
        const direction = [];
        if (ratio > 0.25) {
          // you can adjust the ratio thresholds to make things more or less sensitive to diagonal movement.
          direction.push(xChange < 0 ? "left" : "right");
        }

        if (direction[0] === "left") {
          that._animateSlides("prev");
          return;
        }
        if (direction[0] === "right") {
          that._animateSlides("next");
        }
      },
    });
  };

  /**
    Shifts a slide to the left or right of the screen based on its position.
    @param {HTMLElement} el - The slide to be shifted.
    @param {number} currentKey - The current position of the slide.
    @param {number} nextKey - The position to which the slide should be shifted.
    @returns {Object|boolean} Returns an object containing the left, opacity, and scale values for the slide if the slide is shifted on the screen. Returns false if the slide is not shifted on the screen.
  */
  _shiftPrev = (el, currentKey, nextKey) => {
    // if slide is offscreen left set it offscren right
    if (currentKey === -1) {
      const excessSlides =
        this.sliderEls.allSlides.length - this.animPositions.onScreenAll.length;
      gsap.set(el, { left: this.animPositions.offScreen.right });
      el.dataset.pos = this.sliderEls.allSlides.length - excessSlides;
      return false;
    }

    // if next slide position is greater than number of position markers send offscreen right
    if (nextKey >= this.animPositions.onScreenAll.length) {
      el.dataset.pos = nextKey;

      return {
        left: this.animPositions.offScreen.right,
        scale: 1,
        opacity: 0.25,
      };
    }

    // if slide is first move offscreen left
    if (nextKey >= 0) {
      const { x, opacity, scale } = this.animData[nextKey];
      el.dataset.pos = nextKey;

      return {
        left: x,
        opacity,
        scale,
      };
    }

    el.dataset.pos = nextKey;
    return {
      left: this.animPositions.offScreen.left,
      scale: 1,
      opacity: 0.25,
    };
  };

  /**
    Shifts a slide to the left or right of the screen based on its position.
    @param {HTMLElement} el - The slide to be shifted.
    @param {number} currentKey - The current position of the slide.
    @param {number} nextKey - The position to which the slide should be shifted.
    @returns {Object} Returns an object containing the left, opacity, and scale values for the slide after it has been shifted.
  */
  _shiftNext = (el, currentKey, nextKey) => {
    if (this.animData[nextKey] !== undefined) {
      const { x, opacity, scale } = this.animData[nextKey];
      var animSettings = {
        left: x,
        opacity,
        scale,
      };
    }

    if (nextKey >= this.animPositions.onScreenAll.length) {
      animSettings = {
        left: this.animPositions.offScreen.right,
        scale: 1,
        opacity: 0.25,
      };
    }
    if (currentKey === this.sliderEls.allSlides.length - 1) {
      const { x, opacity, scale } = this.animData[0];
      gsap.set(el, { left: this.animPositions.offScreen.left });
      nextKey = 0;

      animSettings = {
        left: x,
        scale,
        opacity,
      };
    }

    el.dataset.pos = nextKey;
    return animSettings;
  };

  /**

  Aanimates the sliding of the carousel when the user clicks on the 'next' or 'previous' button.
  @param {Event} e - The click event on the 'next' or 'previous' button.
  */
  _animateSlides = (e) => {
    // Determine the type of button that was clicked
    const controlType =
      e.currentTarget === undefined ? e : e.currentTarget.dataset.control;
    let animSettings = false;

    // If the window is smaller than the specified breakpoint, shift the slide without any animation
    if (window.innerWidth < this.breakpoint) {
      controlType === "next"
        ? this.sliderEls.slider.next()
        : this.sliderEls.slider.prev();
      return;
    }

    // Define the function to expand and shift the slide
    const shiftExpand = () => {
      const tl = gsap.timeline();
      tl.to(this.contentEls.container, {
        duration: 0.25,
        opacity: 0,
        onComplete: this._displayData,
      });
      tl.to(
        this.blobEls.container,
        { duration: 0.25, transformOrigin: "center center", scale: 10 },
        "<"
      );
      tl.to(this.blobEls.blobs.desktop, {
        duration: 0.1,
        fill: () => {
          return this.state.colors.blob;
        },
      });
      tl.to(this.section, {
        duration: 0,
        background: () => {
          return this.state.colors.background;
        },
      });

      return tl;
    };

    // Define the function to shrink the slide back to its original size
    const shiftShrink = () => {
      const tl = gsap.timeline();
      tl.to(this.contentEls.container, { duration: 0.1, opacity: 1 });
      tl.to(this.blobEls.container, {
        duration: 0.1,
        transformOrigin: "center center",
        scale: 1,
      });
      return tl;
    };

    // Create a timeline for the master animation and add the expand and shrink functions to it
    const master = gsap.timeline({
      paused: true,
      autoRemoveChildren: true,
      onStart: () => {},
    });
    master.add(shiftExpand(this.state));
    master.add(shiftShrink);

    // Loop through each slide in the carousel
    this.sliderEls.allSlides.forEach((el, i) => {
      // Get the current and next position of the slide
      const currentKey = parseInt(el.dataset.pos);
      const nextKey = controlType === "next" ? currentKey + 1 : currentKey - 1;
      // Check if the slide is the active slide
      const isActiveSlide = nextKey === this.animPositions.medianIndex;
      // Get the animation settings for the slide
      animSettings =
        controlType === "next"
          ? this._shiftNext(el, currentKey, nextKey)
          : this._shiftPrev(el, currentKey, nextKey);

      // If the slide is the active slide, update the data
      if (isActiveSlide) {
        el.removeAttribute("aria-hidden");
        this._updateData(el);
      } else {
        el.setAttribute("aria-hidden", true);
      }
      // If the slide is the active slide, restart the master animation
      if (isActiveSlide) {
        master.restart();
      }
      // If there are animation settings for the slide, animate the slide
      if (animSettings) {
        gsap.to(el, animSettings);
      }
    });
  };

  /**
  Updates the state object with data from the active slide
  @param {Element} el - The active slide element to retrieve data from
  @returns {Object} The updated state object containing the title, text, slide element, index, button label and URL, and background and blob colors
  */
  _updateData = (el) => {
    this.state = {
      title: el.dataset.title,
      text: el.dataset.text,
      slide: el,
      index: el.dataset.index,
      btn: {
        label: el.dataset.label,
        url: el.dataset.url,
      },
      colors: {
        background: el.dataset.bg,
        blob: el.dataset.blob,
      },
    };

    return this.state;
  };

  /**
    Updates the content elements with data from the current state object
  */
  _displayData = () => {
    for (let i = 0; i < this.contentEls.container.length; i++) {
      this.contentEls.title[i].innerHTML = this.state.title;
      this.contentEls.text[i].innerHTML = this.state.text;
      this.contentEls.button[i].innerHTML = this.state.btn.label;
      this.contentEls.button[i].setAttribute("href", this.state.btn.url);
    }

    // hidden doubled slides
    this.sliderEls.allSlides.forEach((el) => {
      const currentPos = el.getAttribute("data-pos");
      const doubleItems = document.querySelectorAll(
        `[data-flavor-slide][data-pos="${currentPos}"]`
      );

      el.classList.remove("opacity-0-imp");
      if (doubleItems.length === 2) {
        doubleItems[0].classList.add("opacity-0-imp");
      } else if (doubleItems.length === 3) {
        doubleItems[0].classList.add("opacity-0-imp");
        doubleItems[1].classList.add("opacity-0-imp");
      }
    });
  };

  /**
    Attaches event listeners to control elements for previous and next slide animations.
    Also disables the loading state after controls have been added.
  */
  _addControls = () => {
    // Attach event listener to previous button click and enter keypress
    this.controlEls.prev.addEventListener("click", this._animateSlides);
    this.controlEls.prev.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        this._animateSlides(e);
      }
    });

    // Attach event listener to next button click and enter keypress
    this.controlEls.next.addEventListener("click", this._animateSlides);
    this.controlEls.next.addEventListener("keypress", (e) => {
      if (e.key === "Enter") {
        this._animateSlides(e);
      }
    });

    // Disable loading state after controls have been added
    this._disableLoadingState();
  };

  /**
    Updates the background color of the section element and the fill color of the mobile and desktop blobs.
    Colors are retrieved from the current state of the object.
  */
  _updateColors = () => {
    this.section.style.background = this.state.colors.background;
    this.blobEls.blobs.mobile.style.fill = this.state.colors.blob;
    this.blobEls.blobs.desktop.style.fill = this.state.colors.blob;
  };

  /**
    Builds a KeenSlider and attaches event listeners for slide changes and animations.
    Updates data, display, and colors on slide change or animation start/end.
  */
  _buildSlider = () => {
    this.sliderEls.slider = new KeenSlider("#track", {
      mode: "snap",
      loop: true,
      initial: 2,
      drag: true,
      selector: this.sliderEls.allSlides,
      slides: {
        origin: "center",
        perView: 2,
        spacing: 35,
      },
      created: (slider) => {
        this._updateData(this.sliderEls.allSlides[2]);
        this.state.slide.style.opacity = 1;
        this._displayData();
        this._updateColors();
        this._addControls();
      },
      animationStarted: () => {
        // Scale the blob container and change slide opacity during animation start
        this.blobEls.container.style.transform = "scale(6)";
        this.state.slide.style.opacity = 0.25;
        this.state.slide.setAttribute("aria-hidden", "true");
      },
      animationEnded: () => {
        // Scale the blob container and change slide opacity during animation end
        // if window is tablet sized return svg to 1.5 scale instead of 1 (we are using mobile path for tablet).
        const size =
          window.innerWidth >= 768 && window.innerWidth < 1024 ? "1.5" : "1";
        this.blobEls.container.style.transform = `scale(${size})`;
        this.state.slide.style.opacity = 1;
        this.state.slide.removeAttribute("aria-hidden");
      },
      slideChanged: (slider) => {
        // Update data, display, and colors on slide change
        const newSlide = this.sliderEls.allSlides[slider.track.details.rel];

        this._updateData(newSlide);
        this._displayData();
        this._updateColors();
      },
    });
  };

  /**

    Resets all slides by removing their inline styles and setting opacity to 0.25.
    The animData array is also cleared.

    @returns {Promise<Element[]>} - A Promise that resolves to an array of all slides after they have been reset.
  */
  _resetSlides = async () => {
    this.animData = [];

    this.sliderEls.allSlides.forEach((slide) => {
      slide.removeAttribute("style", "transform");
      slide.removeAttribute("style", "left");
      slide.style.opacity = 0.25;
    });

    return this.sliderEls.allSlides;
  };

  _enableLoadingState = () => {
    this.wrapper.style.opacity = 0;
    this.sliderEls.spinner.style.display = "block";
  };

  _disableLoadingState = () => {
    setTimeout(() => {
      this.wrapper.style.opacity = 1;
      this.controlEls.container.style.opacity = 1;
      this.sliderEls.spinner.style.display = "none";
      this.loading = false;
    }, 100);
  };

  /**
    Sets up event listener for window resize and handles slide resetting and rebuilding the slider based on the window size.
  */
  _setResize = () => {
    let resizeId;

    window.addEventListener("resize", () => {
      this._enableLoadingState();

      clearTimeout(resizeId);
      resizeId = setTimeout(resizedEnded, 500);
    });

    const resizedEnded = async () => {
      // If the window is smaller than the breakpoint and there is no slider instance and no loading in progress
      if (
        window.innerWidth < this.breakpoint &&
        this.sliderEls.slider === null &&
        this.loading === false
      ) {
        this.loading = true;
        // const slides = await this._resetSlides();
        this.sliderEls.draggableInstance[0].disable();

        this._buildSlider();

        return;
      }

      // custom animation setting should run each time the window is resized to reposition slides
      if (window.innerWidth > this.breakpoint && this.loading === false) {
        this.loading = true;
        // const slides = await this._resetSlides();

        // If there is an existing slider instance, enable draggableInstance, else create a new draggable instance
        if (this.sliderEls.draggableInstance) {
          this.sliderEls.draggableInstance[0].enable();
        } else {
          this._setDraggable();
        }

        // If there is an existing slider instance, destroy it
        if (this.sliderEls.slider) {
          this.sliderEls.slider.destroy();
          this.sliderEls.slider = null;
        }

        this._setAnimPositions();

        return;
      }

      // If the window is smaller than the breakpoint and there is an existing slider instance, disable loading state
      if (
        window.innerWidth < this.breakpoint &&
        this.sliderEls.slider != null
      ) {
        return this._disableLoadingState();
      }
    };
  };

  _init = () => {
    gsap.defaults({ overwrite: true });
    gsap.registerPlugin(Draggable);

    if (window.innerWidth < this.breakpoint) {
      this._buildSlider();
    } else {
      this._setDraggable();
      this._setAnimPositions();
    }

    function isMobile() {
      return /iPhone|iPad|iPod|Android|webOS|BlackBerry|Windows Phone/i.test(
        navigator.userAgent
      );
    }

    if (!isMobile()) {
      this._setResize();
    }
  };
}
